import axios from 'axios';
import { getOrCreateEnrollmentId } from './enrollmentUtil';
import { logger } from '../../logger';
import { removePIIDataFromPayload } from '../../../src/transformers/contactInfo.transform';
import { PROFILE_VERIFY_TOKEN_KEY, PATIENT_TYPE_ADULT } from '../../constants';
import * as cdpService from '../../services/CdpService';
import { CDP_EVENTS } from '../../constants';
import isClinicalTrialSource from '../../lib/utils/sourceHelper';

const get = async (url: string): Promise<any> => {
  const extensionData = {
    ApiUrl: url,
  };
  try {
    const response = await axios.get('/api/cdp-api', {
      url,
    });
    return response;
  } catch (error: any) {
    logger.error(`${process.env.NEXT_PUBLIC_CDP_API_BASE_URL}${url}`, {
      error: error?.message,
      payload: {},
    });
    cdpService.event(CDP_EVENTS.API_FAILURE, extensionData);
    throw error;
  }
};

const post = async (url: string, payload: any, isCR = false): Promise<any> => {
  const enrollmentId = getOrCreateEnrollmentId();
  const data = isCR ? {} : { enrollmentId: enrollmentId, ...payload };
  const extensionData = {
    ApiUrl: url,
  };
  try {
    const response = await axios.post('/api/cdp-api', {
      url,
      data,
    });
    return response;
  } catch (error: any) {
    logger.error(`${process.env.NEXT_PUBLIC_CDP_API_BASE_URL}${url}`, {
      error: error?.message,
      payload: data,
    });
    cdpService.event(CDP_EVENTS.API_FAILURE, extensionData);
    throw error;
  }
};

const put = async (url: string, payload: any): Promise<any> => {
  const enrollmentIdVal = getOrCreateEnrollmentId();
  const data = { enrollmentId: enrollmentIdVal, ...payload };
  const extensionData = {
    ApiUrl: url,
  };
  try {
    const response = await axios.put('/api/cdp-api', {
      url,
      data,
    });
    return response;
  } catch (error: any) {
    logger.error(`${process.env.NEXT_PUBLIC_CDP_API_BASE_URL}${url}`, {
      error: error?.message,
      payload: data,
    });
    cdpService.event(CDP_EVENTS.API_FAILURE, extensionData);
    throw error;
  }
};

const savePatientDelegateDetailsToCDP = async (
  url: string,
  payload: any,
  source: string
): Promise<any> => {
  const payloadWithoutPIIData = removePIIDataFromPayload(payload);
  const extensionData = {
    ApiUrl: url,
  };
  try {
    const response = await axios.post('/api/cdp-api', { url, data: payload });
    const myProfileVerificationToken = response?.data?.myProfileVerificationToken;
    if (
      myProfileVerificationToken &&
      isClinicalTrialSource(source) &&
      response?.data?.contactType?.includes(PATIENT_TYPE_ADULT)
    ) {
      sessionStorage.setItem(PROFILE_VERIFY_TOKEN_KEY, myProfileVerificationToken);
    }
    cdpService.event(CDP_EVENTS.CDP_API_CONTACTINFO_SUCCESS, extensionData);
    return response;
  } catch (error: any) {
    logger.error(`${process.env.NEXT_PUBLIC_CDP_API_BASE_URL}${url}`, {
      error: error?.message,
      payload: payloadWithoutPIIData,
    });

    cdpService.event(CDP_EVENTS.CDP_API_CONTACTINFO_FAILURE, extensionData);
    throw error;
  }
};
const decryptTokenForResumeJourney = async (url: string, payload: any): Promise<any> => {
  try {
    const response = await axios.post('/api/cdp-api', { url, data: payload });
    return response;
  } catch (error: any) {
    logger.error(`${process.env.NEXT_PUBLIC_CDP_API_BASE_URL}${url}`, {
      error: error?.message,
      payload: payload,
    });
    throw error;
  }
};
const checkForResumeUserForTALevelEPR = async (url: string, payload: any): Promise<any> => {
  try {
    const response = await axios.post('/api/cdp-api', { url, data: payload });
    return response;
  } catch (error: any) {
    logger.error(`${process.env.NEXT_PUBLIC_CDP_API_BASE_URL}${url}`, {
      error: error?.message,
      payload: payload,
    });
    throw error;
  }
};

const updateParticipantType = async (
  guestRef: string,
  enrollmentId: string,
  participantType: string,
  guestType = 'visitor'
): Promise<void> => {
  const payload = {
    enrollmentId: enrollmentId,
    participantType: participantType,
  };
  const url = `/api/contacts/UpdateParticipantType/${guestRef}?guestType=${guestType}`;
  return await post(url, payload);
};

const addOrUpdateCrRegisterUser = async (url: string, payload: any): Promise<any> => {
  try {
    const response = axios.post('/api/cdp-api', { url, data: payload });
    return response;
  } catch (error: any) {
    logger.error(`${process.env.NEXT_PUBLIC_CDP_API_BASE_URL}${url}`, {
      error: error?.message,
      payload: payload,
    });
    throw error;
  }
};

const verifyMyProfileEmail = async (url: string, payload: any): Promise<any> => {
  try {
    const response = await axios.post('/api/cdp-api', { url, data: payload });
    return response;
  } catch (error: any) {
    logger.error(`${process.env.NEXT_PUBLIC_CDP_API_BASE_URL}${url}`, {
      error: error?.message,
      payload: payload.guestRef,
    });
    throw error;
  }
};

const fetchProfileDetailsForCR = async (verificationToken: string): Promise<any> => {
  const url = `/api/FetchProfileDetailsForCR`;
  const payload = {
    verificationToken,
  };
  const extensionData = {
    ApiUrl: url,
  };
  try {
    const response = await axios.post('/api/cdp-api', { url, data: payload });
    cdpService.event(CDP_EVENTS.CDP_API_FETCHPROFILE_SUCCESS, extensionData);
    return response;
  } catch (error: any) {
    logger.error(`${process.env.NEXT_PUBLIC_CDP_API_BASE_URL}${url}`, {
      error: error?.message,
      payload: payload,
    });
    cdpService.event(CDP_EVENTS.CDP_API_FETCHPROFILE_FAILURE, extensionData);
    throw error;
  }
};

const prePopulateEprContactDetails = async (
  verificationToken: string,
  tenantkey: string
): Promise<any> => {
  const url = `/api/PrePopulateEprContactDetails`;
  const payload = {
    verificationToken,
    tenantkey,
  };
  try {
    const response = await axios.post('/api/cdp-api', { url, data: payload });
    return response;
  } catch (error: any) {
    logger.error(`${process.env.NEXT_PUBLIC_CDP_API_BASE_URL}${url}`, {
      error: error?.message,
      payload: payload,
    });
    throw error;
  }
};
const updateSavedTrialsForMyProfile = async (payload: any): Promise<any> => {
  const url = `/api/contacts/UpdateSavedTrialsForMyProfile`;
  try {
    const response = await axios.post('/api/cdp-api', { url, data: payload });
    return response;
  } catch (error: any) {
    logger.error(`${process.env.NEXT_PUBLIC_CDP_API_BASE_URL}${url}`, {
      error: error?.message,
      payload: payload,
    });
    throw error;
  }
};
const updateBrowserIdForMigratedUser = async (payload: any): Promise<any> => {
  const url = `/api/contacts/UpdateBrowserIdForMigratedUsers`;
  try {
    const response = await axios.post('/api/cdp-api', { url, data: payload });
    return response;
  } catch (error: any) {
    logger.error(`${process.env.NEXT_PUBLIC_CDP_API_BASE_URL}${url}`, {
      error: error?.message,
      payload: payload,
    });
    throw error;
  }
};

export {
  get,
  post,
  put,
  updateParticipantType,
  savePatientDelegateDetailsToCDP,
  decryptTokenForResumeJourney,
  addOrUpdateCrRegisterUser,
  checkForResumeUserForTALevelEPR,
  verifyMyProfileEmail,
  fetchProfileDetailsForCR,
  updateSavedTrialsForMyProfile,
  prePopulateEprContactDetails,
  updateBrowserIdForMigratedUser,
};
